import React, { useMemo } from "react";
import { buildFacility360Metrics } from "../utils/facility360Metrics";

function normalize(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function titleCase(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function StatusRow({
  label,
  complete,
  detail,
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12,
        padding: "11px 12px",
        borderRadius: 12,
        background: "#ffffff",
        border: "1px solid #e2e8f0",
      }}
    >
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 12,
            fontWeight: 900,
          }}
        >
          {label}
        </div>

        {detail ? (
          <div
            style={{
              marginTop: 3,
              color: "#64748b",
              fontSize: 10,
              fontWeight: 700,
              lineHeight: 1.4,
            }}
          >
            {detail}
          </div>
        ) : null}
      </div>

      <div
        style={{
          padding: "5px 8px",
          borderRadius: 999,
          color: complete
            ? "#166534"
            : "#991b1b",
          background: complete
            ? "#dcfce7"
            : "#fee2e2",
          fontSize: 9,
          fontWeight: 950,
          whiteSpace: "nowrap",
          textTransform: "uppercase",
        }}
      >
        {complete ? "Complete" : "Attention"}
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  color = "#0f172a",
}) {
  return (
    <div
      style={{
        padding: 13,
        borderRadius: 14,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 10,
          fontWeight: 900,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          color,
          fontSize: 21,
          fontWeight: 950,
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default function Facility360Compliance({
  facility,
  users = [],
}) {
  const analysis = useMemo(() => {
    const sharedMetrics =
      buildFacility360Metrics({
        facility,
        shifts: [],
        users,
      });

    const {
      facilityWorkers,
      compliantWorkers: approvedWorkers,
      pendingWorkers,
      attentionWorkers,
      workforceCompliance,
    } = sharedMetrics;

  const requiredRoles =
  Array.isArray(facility?.allowedRoles) &&
  facility.allowedRoles.length > 0
    ? facility.allowedRoles
    : [
        "CNA",
        "LVN",
        "RN",
        "MED_AIDE",
        "CARE_GIVER",
      ];

    const missingRates =
      requiredRoles.filter((role) => {
        const rateRecord =
          facility?.rates?.[role] || {};

        const billRate = Number(
          rateRecord?.billRate
        );

        const payRate = Number(
          rateRecord?.payRate
        );

        return !(
          Number.isFinite(billRate) &&
          billRate > 0 &&
          Number.isFinite(payRate) &&
          payRate > 0
        );
      });

    const profileChecks = [
      {
        label: "Facility Identity",
        complete: Boolean(
          facility?.name ||
            facility?.facilityName
        ),
        detail:
          "Facility name must be configured.",
      },
      {
        label: "Tenant Assignment",
        complete: Boolean(
          facility?.tenantId
        ),
        detail:
          "Facility must belong to a tenant.",
      },
      {
        label: "Facility Address",
        complete: Boolean(
          facility?.address &&
            facility?.city &&
            facility?.state &&
            (facility?.zip ||
              facility?.zipCode)
        ),
        detail:
          "Street, city, state, and ZIP are required.",
      },

{
  label: "Geofence Coordinates",
  complete:
    facility?.facilityLat !== "" &&
    facility?.facilityLat !== null &&
    facility?.facilityLat !== undefined &&
    facility?.facilityLng !== "" &&
    facility?.facilityLng !== null &&
    facility?.facilityLng !== undefined &&
    Number.isFinite(
      Number(facility.facilityLat)
    ) &&
    Number(facility.facilityLat) >= -90 &&
    Number(facility.facilityLat) <= 90 &&
    Number.isFinite(
      Number(facility.facilityLng)
    ) &&
    Number(facility.facilityLng) >= -180 &&
    Number(facility.facilityLng) <= 180,
  detail:
    "Valid latitude and longitude support arrival validation.",
},
      {
        label: "Arrival Radius",
        complete:
          Number(
            facility?.geoRadiusMeters
          ) > 0,
        detail:
          "A positive geofence radius is required.",
      },
      {
        label: "Rates Matrix",
        complete:
          missingRates.length === 0,
        detail:
          missingRates.length === 0
            ? "Bill and pay rates are configured."
            : `Missing rates: ${missingRates.join(
                ", "
              )}`,
      },
  {
  label: "Active Status",
  complete:
    normalize(
      facility?.status || "active"
    ) === "active",
  detail:
    "Inactive facilities should not receive new shifts.",
},
    ];

    const completedChecks =
      profileChecks.filter(
        (check) => check.complete
      ).length;

    const configurationScore =
      profileChecks.length > 0
        ? Math.round(
            (completedChecks /
              profileChecks.length) *
              100
          )
        : 0;

    return {
      facilityWorkers,
      approvedWorkers,
      pendingWorkers,
      attentionWorkers,
      missingRates,
      profileChecks,
      configurationScore,
      workforceCompliance,
    };
  }, [facility, users]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          Facility Compliance
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Configuration and workforce readiness for{" "}
          {facility?.name ||
            facility?.facilityName ||
            facility?.id ||
            "this facility"}
          .
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 9,
        }}
      >
        <MetricCard
          label="Configuration"
          value={`${analysis.configurationScore}%`}
          color={
            analysis.configurationScore ===
            100
              ? "#166534"
              : "#b45309"
          }
        />

        <MetricCard
          label="Workforce Compliance"
          value={`${analysis.workforceCompliance}%`}
          color={
            analysis.workforceCompliance >=
            90
              ? "#166534"
              : "#b45309"
          }
        />

        <MetricCard
          label="Approved Workers"
          value={
            analysis.approvedWorkers.length
          }
          color="#166534"
        />

        <MetricCard
          label="Pending Review"
          value={
            analysis.pendingWorkers.length
          }
          color={
            analysis.pendingWorkers.length >
            0
              ? "#b45309"
              : "#166534"
          }
        />

        <MetricCard
          label="Needs Attention"
          value={
            analysis.attentionWorkers.length
          }
          color={
            analysis.attentionWorkers.length >
            0
              ? "#b91c1c"
              : "#166534"
          }
        />

        <MetricCard
          label="Missing Rate Roles"
          value={analysis.missingRates.length}
          color={
            analysis.missingRates.length > 0
              ? "#b91c1c"
              : "#166534"
          }
        />
      </div>

      <div
        style={{
          padding: 14,
          borderRadius: 16,
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            marginBottom: 10,
            color: "#0f172a",
            fontSize: 14,
            fontWeight: 950,
          }}
        >
          Facility Readiness Checklist
        </div>

        <div
          style={{
            display: "grid",
            gap: 8,
          }}
        >
          {analysis.profileChecks.map(
            (check) => (
              <StatusRow
                key={check.label}
                label={check.label}
                complete={check.complete}
                detail={check.detail}
              />
            )
          )}
        </div>
      </div>

      {analysis.attentionWorkers.length >
      0 ? (
        <div
          style={{
            padding: 14,
            borderRadius: 16,
            background: "#fef2f2",
            border: "1px solid #fecaca",
          }}
        >
          <div
            style={{
              color: "#991b1b",
              fontSize: 14,
              fontWeight: 950,
            }}
          >
            Workers Needing Attention
          </div>

          <div
            style={{
              marginTop: 9,
              display: "grid",
              gap: 7,
            }}
          >
            {analysis.attentionWorkers.map(
              (worker) => (
                <div
                  key={worker.uid || worker.id}
                  style={{
                    color: "#7f1d1d",
                    fontSize: 12,
                    fontWeight: 800,
                  }}
                >
                  •{" "}
                  {worker.displayName ||
                    worker.fullName ||
                    worker.email ||
                    "Worker"}
                  {" — "}
                  {titleCase(
                    worker.complianceStatus ||
                      worker.workerStatus ||
                      worker.status
                  )}
                </div>
              )
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}